import React, { useRef, useEffect, useState, Suspense } from 'react';
import { Router, Switch } from 'react-router-dom';
import {
  useTheme,
  useAuthUser,
  useAuthSession,
  useAuthAccountId,
  useAuthUserClaims
} from '@nbx/frontend-helpers/subscribe';
import { NBXStorage } from '@nbx/frontend-helpers/storage';
import { useReferralCardPartnership } from '@nbx/frontend-helpers/services';
import { useIsMobile } from 'helpers/hooks';
import HeaderAndNavigation from 'components/app/HeaderAndNavigation';
import Footer from 'components/app/Footer';
import NotificationProvider from 'components/app/NotificationProvider';
import PrivateRoute from 'components/app/PrivateRoute';
import StatusBar from 'components/app/Header/StatusBar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Capacitor } from '@capacitor/core';
import { useLastActiveTime } from 'helpers/useLastActiveTime';
import { useBiometricGate } from '@nbx/frontend-helpers/auth';
import { BiometricGate } from '@nbx/ui-components';
import { PushNotificationsProvider } from 'components/app/PushNotificationsProvider';
import { ModalSwitcher } from 'components/app/ModalSwitcher';
import { useRedirectOnboardingFlow } from './helpers/useRedirectOnboardingFlow';
import { BottomNavPortal } from './components/app/BottomNav';
import Store from './state/store';

const hideStatusBarRoutes = [];
const privateRoutes = [];
const landingPageRoutes = [];
const googleAdsRoutes = [];
const tradingRoutes = [];
const blankPageRoutes = [];
const dashboardsRoutes = [];

const doesPathMatch = (routeList, path) => {
  return routeList.some(route => {
    if (route.includes(':') && !!path) {
      const splitRoutePrefix = route.split(':')[0];
      return path?.includes(splitRoutePrefix);
    }
    if (route === path || `${route}/` === path) return true;
    return false;
  });
};

const App = () => {
  const isMobile = useIsMobile();
  const [isLandingPage, setIsLandingPage] = useState(false);
  const [isBlankPage, setIsBlankPage] = useState(false);
  const [isTradingPage, setIsTradingPage] = useState(false);
  const [isDashboardsPage, setIsDashboardsPage] = useState(false);
  const [transparentHeader, setTransparentHeader] = useState(false);
  const [hideStatusBar, setHideStatusBar] = useState(true);
  const theme = useTheme();
  const accountId = useAuthAccountId();
  const user = useAuthUser();
  const claims = useAuthUserClaims();
  const session = useAuthSession();

  const biometricGate = useBiometricGate({ tag: 'frame' });

  const referralCardPartnership = useReferralCardPartnership({ enabled: !!user && !!session });

  useRedirectOnboardingFlow(
    {
      enabled: !referralCardPartnership.loading,
      isCardPartnershipOnboarding: !!referralCardPartnership.code
    },
    [isDashboardsPage]
  );

  const prevPathname = useRef(location.pathname);

  const microapps = window.MICROFRONTENDS_CONFIG;

  useEffect(() => {
    if (Capacitor.isNativePlatform()) SplashScreen.hide({ fadeOutDuration: 1000 });
  }, []);

  // Setup route listeners && route flags
  useEffect(() => {
    let unlistenToHistory;

    if (microapps) {
      unlistenToHistory = window.HISTORY.listen(onRouteChange);
      Object.keys(microapps?.routes).forEach(routeKey => {
        // TODO: find a way to not hardcode a reference to the landingpage entry
        const route = microapps?.routes[routeKey];
        const localePaths = window.GET_LOCALE_PATHS(microapps?.routes[routeKey].path);
        if (route.app === 'landingpage') landingPageRoutes.push(...localePaths);
        if (route.app === 'landingpageGoogleAds') {
          landingPageRoutes.push(...localePaths);
          googleAdsRoutes.push(...localePaths);
        }
        if (route.app === 'blog') landingPageRoutes.push(...localePaths);
        if (routeKey === 'trading') tradingRoutes.push(...localePaths);
        if (routeKey === 'dashboards') dashboardsRoutes.push(...localePaths);
        if (route.hideStatusBar) hideStatusBarRoutes.push(...localePaths);
        if (route.private)
          privateRoutes.push(
            ...localePaths.map(path => {
              return { path, component: () => null };
            })
          );
        if (route.blankPage) {
          blankPageRoutes.push(...localePaths);
        }
      });
      onRouteChange(location, null, true);
    }
    return () => unlistenToHistory?.();
  }, [microapps]); // eslint-disable-line

  // Set the theme class in HTML when theme UI is toggled
  useEffect(() => {
    const newTheme = theme;
    if (newTheme) {
      const html = document.getElementsByTagName('html')[0];
      html.classList.remove('dark');
      html.classList.remove('light');
      html.classList.add(newTheme);
    }
  }, [theme]);

  const isBlankPagePath = path => {
    return doesPathMatch(blankPageRoutes, path);
  };

  // Redirect to dashboard if we need to show password
  useEffect(() => {
    const handlePossibleRedirects = async () => {
      const passwordInsecure = await NBXStorage.getItem('password-insecure');
      if (
        user &&
        passwordInsecure &&
        !window.location.pathname.includes(microapps?.routes?.dashboards?.path)
      ) {
        window.HISTORY.replace(microapps?.routes?.dashboards?.path);
      }
    };
    handlePossibleRedirects();
  }, [user, microapps]);

  useLastActiveTime({ platform: window.PLATFORM, accountId });

  // When route changes, check and set all the state vars based on route
  const onRouteChange = (route, navEventType, initial) => {
    if (route.pathname !== prevPathname.current || initial) {
      setIsLandingPage(doesPathMatch(landingPageRoutes, route.pathname));
      setIsTradingPage(doesPathMatch(tradingRoutes, route.pathname));
      setIsDashboardsPage(doesPathMatch(dashboardsRoutes, route.pathname));
      setTransparentHeader(doesPathMatch(googleAdsRoutes, route.pathname));
      setHideStatusBar(doesPathMatch(hideStatusBarRoutes, route.pathname));
      setIsBlankPage(isBlankPagePath(route.pathname));
      const childRouteTransition =
        (prevPathname.current.includes(route.pathname?.split('/')[1] ?? '') ||
          route.pathname.includes(prevPathname.current?.split('/')[1] ?? '')) &&
        prevPathname.current !== '/' &&
        route.pathname !== '/';
      prevPathname.current = route.pathname;
      if (!childRouteTransition) window.scroll({ top: 0 });
    }
  };

  return (
    <>
      {!isBlankPage && biometricGate.show && session && (
        <nbx-theme-provider theme={theme}>
          <BiometricGate
            onRequestBiometricAuth={biometricGate.authenticate}
            loading={biometricGate.loading}
            error={biometricGate.error}
            requestOnInit={biometricGate.ask}
            tag="frame"
          />
        </nbx-theme-provider>
      )}
      <Store>
        <Router history={window.HISTORY}>
          <Suspense fallback={<nbx-spinner />}>
            {window.PLATFORM !== 'web' && <PushNotificationsProvider />}
            {!isBlankPage && <StatusBar hideStatusBar={hideStatusBar} usePortal={isMobile} />}
            {!isBlankPage && (
              <nbx-theme-provider theme={theme}>
                <HeaderAndNavigation
                  isMobile={isMobile}
                  isLandingPage={isLandingPage}
                  isTradingPage={isTradingPage}
                  user={user}
                  headerProps={{
                    isTransparent: transparentHeader
                  }}
                />
                <ModalSwitcher />
              </nbx-theme-provider>
            )}
            {!isBlankPage && <NotificationProvider />}
            {isMobile && session && <BottomNavPortal />}
            {window.PLATFORM === 'web' && !isBlankPage && (
              <Footer
                user={user}
                isGoogleAdsLandingPage={transparentHeader}
                isLandingPage={isLandingPage}
                isTradingPage={isTradingPage}
                isMobile={isMobile}
              />
            )}
            <Switch>
              {privateRoutes.map(route => (
                <PrivateRoute
                  path={route.path}
                  key={route.path}
                  accountId={accountId}
                  user={user}
                  claims={claims}
                  session={session}
                />
              ))}
            </Switch>
          </Suspense>
        </Router>
      </Store>
    </>
  );
};

export default App;
